import React, { useState } from "react";
import { IconButton, Badge } from "@mui/material";
import ChatIcon from "@mui/icons-material/ChatOutlined";
import { useChat } from "@/hooks/useChat";
import { ChatPopover } from "./ChatPopover";
import { ChatWindow } from "./ChatWindow";
import { UserSelectDialog } from "./UserSelectDialog";
import { CreateGroupDialog } from "./CreateGroupDialog";
import { useAuth } from "@/hooks/auth";

export const ChatWidget: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isUserSelectOpen, setIsUserSelectOpen] = useState(false);
  const [isGroupCreateOpen, setIsGroupCreateOpen] = useState(false);
  const { user } = useAuth();
  
  const {
    conversations,
    activeConversationIds,
    messages,
    typingUsers,
    sendMessage,
    reactToMessage,
    setTypingStatus,
    openConversation,
    closeConversation,
    startConversationWithUser,
    startGroupConversation,
    addGroupMembers,
    removeGroupMember,
    deleteGroupConversation,
    loadMoreMessages,
    hasMore,
    isFetchingMore,
  } = useChat();

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const unreadConversationsCount = conversations.filter((conv) => (conv.unreadCount || 0) > 0).length;

  return (
    <>
      <IconButton size="large" onClick={handleOpen}>
        <Badge badgeContent={unreadConversationsCount} color="primary">
          <ChatIcon />
        </Badge>
      </IconButton>
      
      <ChatPopover
        anchorEl={anchorEl}
        onClose={handleClose}
        conversations={conversations}
        onOpenConversation={openConversation}
        onNewChat={() => setIsUserSelectOpen(true)}
        onNewGroupChat={() => setIsGroupCreateOpen(true)}
        currentUserId={user?.id as string | undefined}
      />

      {activeConversationIds.map((id, index) => {
        const conversation = conversations.find(c => c.id === id);
        return (
          <ChatWindow
            key={id}
            positionIndex={index}
            conversation={conversation}
            messages={messages[id] || []}
            typingUsers={typingUsers[id] || []}
            onClose={() => closeConversation(id)}
            onSendMessage={sendMessage}
            onReact={reactToMessage}
            onTyping={setTypingStatus}
            onLoadMore={loadMoreMessages}
            onAddMembers={addGroupMembers}
            onRemoveMember={removeGroupMember}
            onDeleteGroup={deleteGroupConversation}
            hasMore={hasMore[id] || false}
            isLoadingMore={isFetchingMore}
          />
        );
      })}

      <UserSelectDialog
        open={isUserSelectOpen}
        onClose={() => setIsUserSelectOpen(false)}
        onSelectUser={(userId) => {
          setIsUserSelectOpen(false);
          setAnchorEl(null);
          startConversationWithUser(userId);
        }}
      />

      <CreateGroupDialog
        open={isGroupCreateOpen}
        onClose={() => setIsGroupCreateOpen(false)}
        onCreateGroup={(name, description, participantIds) => {
          setIsGroupCreateOpen(false);
          setAnchorEl(null);
          startGroupConversation(name, description, participantIds);
        }}
      />
    </>
  );
};
